import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  BarChart2, Coins, ArrowUpFromLine, Package, Truck, Users, Receipt, Wrench, 
  TrendingUp, Ban, Briefcase, Settings, Eye, Lock, FileText, 
  Folder, Scale, Clock
} from 'lucide-react';

const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isPaymentBlocked, setIsPaymentBlocked] = useState(false);
  const [blockingInfo, setBlockingInfo] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState(null);

  // Vérifier si on est sur une page finances (autorisée même si bloqué)
  const isFinancePage = location.pathname.includes('/finances');

  // Menu items pour le workspace projet (sans boutique et portefeuille)
  const menuItems = [
    { path: `/projects/${projectId}/workspace`, icon: <BarChart2 className="w-5 h-5" />, label: 'Tableau de bord', exact: true },
    { path: `/projects/${projectId}/workspace/payments`, icon: <Coins className="w-5 h-5" />, label: 'Encaissements' },
    { path: `/projects/${projectId}/workspace/expenses`, icon: <ArrowUpFromLine className="w-5 h-5" />, label: 'Dépenses' },
    { path: `/projects/${projectId}/workspace/products`, icon: <Package className="w-5 h-5" />, label: 'Produits' },
    { path: `/projects/${projectId}/workspace/restock`, icon: <Truck className="w-5 h-5" />, label: 'Ravitaillement' },
    { path: `/projects/${projectId}/workspace/clients`, icon: <Users className="w-5 h-5" />, label: 'Clients' },
    { path: `/projects/${projectId}/workspace/invoices`, icon: <Receipt className="w-5 h-5" />, label: 'Factures' },
    { path: `/projects/${projectId}/workspace/prestations`, icon: <Wrench className="w-5 h-5" />, label: 'Prestations' },
    { path: `/projects/${projectId}/workspace/report`, icon: <TrendingUp className="w-5 h-5" />, label: 'Bilan' },
    { path: `/projects/${projectId}/workspace/sales-analytics`, icon: <BarChart2 className="w-5 h-5" />, label: 'Analyse Ventes' },
    { path: `/projects/${projectId}/finances/collaborators`, icon: <Users className="w-5 h-5" />, label: 'Parts Collaborateurs', highlight: true },
  ];

  useEffect(() => {
    loadProjectAndPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadProjectAndPermissions = async () => {
    try {
      setLoading(true);
      
      // Vérifier l'accès au workspace d'abord
      try {
        const accessRes = await api.get(`/project-finances/${projectId}/access`);
        
        // Si le projet est bloqué et on n'est pas sur une page finances
        if (accessRes.data.blocked && accessRes.data.reason === 'payment_blocked') {
          setIsPaymentBlocked(true);
          setBlockingInfo(accessRes.data.blocking_info);
          
          // Si on n'est pas sur une page finances, rediriger
          if (!isFinancePage) {
            setLoading(false);
            return;
          }
        }
        
        setIsAdmin(accessRes.data.isAdmin || false);
      } catch (err) {
        if (err.response?.status === 403) {
          setError('Vous n\'avez pas accès à ce projet.');
          setLoading(false);
          return;
        }
      }
      
      // Charger les infos du projet
      const projectRes = await api.get(`/projects/${projectId}`);
      setProject(projectRes.data);
      
      // Vérifier si le projet est suspendu
      if (projectRes.data.is_suspended) {
        setIsSuspended(true);
        setSuspensionReason(projectRes.data.suspension_reason);
        setLoading(false);
        return;
      }
      
      // Charger le dashboard pour obtenir les permissions
      const dashRes = await api.get(`/projects/${projectId}/workspace/dashboard`);
      setCanManage(dashRes.data.can_manage || false);
      setIsOwner(dashRes.data.is_owner || false);
      setIsReadOnly(dashRes.data.access?.isReadOnly || false);
      
      setError(null);
    } catch (err) {
      console.error('Erreur chargement projet:', err);
      if (err.response?.status === 403) {
        setError('Vous n\'avez pas accès aux finances de ce projet.');
      } else if (err.response?.status === 404) {
        setError('Projet non trouvé.');
      } else {
        setError('Erreur lors du chargement du projet.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-500">Chargement du workspace...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <span className="text-6xl mb-4 block"><Ban className="w-16 h-16 mx-auto text-red-500" /></span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/project/${projectId}`)}
            className="px-6 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition"
          >
            Retour au projet
          </button>
        </div>
      </div>
    );
  }

  // Écran de blocage pour projet suspendu
  if (isSuspended) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full text-center border-t-4 border-gray-500">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Ban className="w-10 h-10 text-gray-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Projet suspendu</h2>
          <p className="text-gray-600 mb-4">
            Ce projet a été suspendu et l'accès au workspace est temporairement désactivé.
          </p>
          
          {suspensionReason && (
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-gray-700 font-medium mb-2">
                <FileText className="w-4 h-4" />
                <span>Raison de la suspension :</span>
              </div>
              <p className="text-gray-600 text-sm">{suspensionReason}</p>
            </div>
          )}
          
          <p className="text-sm text-gray-500 mb-6">
            Si vous pensez qu'il s'agit d'une erreur, veuillez contacter le support.
          </p>
          
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(`/project/${projectId}`)}
              className="px-6 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition"
            >
              ← Retour au projet
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition"
            >
              Accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Écran de blocage pour non-paiement (sauf si on est sur /finances)
  if (isPaymentBlocked && !isFinancePage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full text-center border-t-4 border-red-500">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Ban className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Projet temporairement bloqué</h2>
          <p className="text-gray-600 mb-4">
            Ce projet est bloqué pour <strong>non-paiement de la commission plateforme (3%)</strong>.
          </p>
          
          {blockingInfo && (
            <div className="bg-red-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                <Clock className="w-4 h-4" />
                <span>Bloqué depuis {blockingInfo.days_blocked} jour(s)</span>
              </div>
              <p className="text-sm text-red-600">
                Dernier paiement : {blockingInfo.last_payment_date 
                  ? new Date(blockingInfo.last_payment_date).toLocaleDateString('fr-FR')
                  : 'Aucun'}
              </p>
            </div>
          )}
          
          {/* Avertissement légal */}
          <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-3">
              <Scale className="w-6 h-6 text-yellow-700 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-yellow-800 mb-1">Avertissement important</h3>
                <p className="text-sm text-yellow-700">
                  En acceptant d'utiliser DJAMANN, vous avez accepté les <strong>conditions générales d'utilisation</strong> incluant le paiement de la commission plateforme de 3%.
                </p>
                <p className="text-sm text-yellow-700 mt-2">
                  <strong>Continuer à faire fonctionner ce projet en dehors de la plateforme</strong> (par email, téléphone, ou tout autre moyen) pour éviter de payer cette commission constitue une <strong>violation contractuelle</strong> et peut entraîner des <strong>poursuites judiciaires</strong>.
                </p>
                <p className="text-sm text-yellow-800 mt-2 font-medium flex items-center gap-1">
                  <Lock className="w-4 h-4" /> Toutes les activités et distributions de bénéfices sont enregistrées et serviront de preuves en cas de litige.
                </p>
              </div>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm mb-6">
            Pour débloquer votre projet et continuer à travailler légalement, effectuez votre paiement plateforme dans la section Finances.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={`/projects/${projectId}/finances/payments`}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition font-medium shadow-lg flex items-center gap-2"
            >
              <Coins className="w-5 h-5" /> Effectuer le paiement
            </Link>
            <button
              onClick={() => navigate(`/project/${projectId}`)}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
            >
              Retour au projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex pt-16">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:top-16 lg:bottom-0 bg-white border-r">
        {/* Header sidebar */}
        <div className="p-4 border-b">
          <Link to={`/project/${projectId}`} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mb-3">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour au projet
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 text-sm truncate max-w-[150px]">{project?.title}</h2>
              <p className="text-xs text-gray-500">Workspace Finances</p>
            </div>
          </div>
        </div>

        {/* Permission badge */}
        {(isAdmin || isReadOnly) && (
          <div className={`mx-4 mt-4 px-3 py-2 rounded-lg ${isAdmin ? 'bg-indigo-50 border border-indigo-200' : 'bg-amber-50 border border-amber-200'}`}>
            <p className={`text-xs ${isAdmin ? 'text-indigo-700' : 'text-amber-700'}`}>
              <span className="font-medium flex items-center gap-1">{isAdmin ? <><Lock className="w-3 h-3" /> Mode Admin (lecture seule)</> : <><Eye className="w-3 h-3" /> Mode lecture</>}</span><br />
              {isAdmin ? 'Consultation en tant qu\'administrateur.' : 'Vous pouvez consulter mais pas modifier.'}
            </p>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl transition ${
                    isActive(item.path, item.exact)
                      ? 'bg-purple-100 text-purple-700'
                      : item.highlight
                        ? 'bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 border border-violet-300 hover:from-violet-200 hover:to-purple-200'
                        : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Permissions link (owner only) */}
        {isOwner && (
          <div className="p-4 border-t">
            <Link
              to={`/projects/${projectId}/workspace/permissions`}
              className="flex items-center gap-3 px-3 py-2 rounded-xl text-gray-600 hover:bg-gray-100 transition"
            >
              <Settings className="w-5 h-5" />
              <span className="text-sm font-medium">Gérer les accès</span>
            </Link>
          </div>
        )}
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-16 left-0 right-0 bg-white border-b z-30 shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 -ml-2"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
              <h1 className="font-bold text-gray-800 text-sm truncate max-w-[180px]">{project?.titre || project?.title}</h1>
              <p className="text-xs text-purple-600 flex items-center gap-1"><Briefcase className="w-3 h-3" /> Workspace</p>
            </div>
          </div>
          <Link
            to={`/project/${projectId}`}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        >
          <aside 
            className="w-72 h-full bg-white overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 text-sm">{project?.title}</h2>
                  <p className="text-xs text-gray-500">Workspace</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Permission badge */}
            {(isAdmin || isReadOnly) && (
              <div className={`mx-4 mt-4 px-3 py-2 rounded-lg ${isAdmin ? 'bg-indigo-50 border border-indigo-200' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-xs ${isAdmin ? 'text-indigo-700' : 'text-amber-700'}`}>
                  <span className="font-medium flex items-center gap-1">{isAdmin ? <><Lock className="w-3 h-3" /> Mode Admin</> : <><Eye className="w-3 h-3" /> Mode lecture</>}</span>
                </p>
              </div>
            )}

            {/* Navigation */}
            <nav className="p-4">
              <ul className="space-y-1">
                {menuItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl transition ${
                        isActive(item.path, item.exact)
                          ? 'bg-purple-100 text-purple-700'
                          : item.highlight
                            ? 'bg-gradient-to-r from-violet-100 to-purple-100 text-violet-700 border border-violet-300 hover:from-violet-200 hover:to-purple-200'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Permissions link */}
            {isOwner && (
              <div className="p-4 border-t">
                <Link
                  to={`/projects/${projectId}/workspace/permissions`}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl text-gray-600 hover:bg-gray-100"
                >
                  <Settings className="w-5 h-5" />
                  <span className="text-sm font-medium">Gérer les accès</span>
                </Link>
              </div>
            )}

            {/* Back to project */}
            <div className="p-4">
              <Link
                to={`/projects/${projectId}`}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour au projet
              </Link>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        {/* Bandeau avec le nom du projet */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 lg:px-6 py-3 lg:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:gap-3 min-w-0">
              <Folder className="w-6 h-6 lg:w-7 lg:h-7 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base lg:text-xl font-bold truncate">{project?.titre || project?.title}</h1>
                <p className="text-purple-200 text-xs lg:text-sm hidden sm:block">Workspace Finances du projet</p>
              </div>
            </div>
            <Link 
              to={`/project/${projectId}`}
              className="px-3 lg:px-4 py-1.5 lg:py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs lg:text-sm font-medium transition flex items-center gap-1 lg:gap-2 flex-shrink-0"
            >
              <svg className="w-3 h-3 lg:w-4 lg:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="hidden sm:inline">Voir le projet</span>
              <span className="sm:hidden">Projet</span>
            </Link>
          </div>
        </div>
        
        {/* Context passé aux enfants */}
        <Outlet context={{ projectId, project, canManage, isOwner, isAdmin, isReadOnly, refreshProject: loadProjectAndPermissions }} />
      </main>
    </div>
  );
};

export default ProjectWorkspace;

